import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import './PuppyModal.css'

const PuppyModal = ({ puppy, onClose }) => {
  const navigate = useNavigate()
  
  if (!puppy) return null

  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.8,
      y: 50
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  }

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleInquiry = () => {
    onClose()
    if (puppy.status === 'Available') {
      navigate(`/puppy/${puppy.id}`)
    } else {
      // For reserved/sold puppies, scroll to contact section
      const contactSection = document.getElementById('contact')
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return 'status-available'
      case 'Reserved':
        return 'status-reserved'
      case 'Sold':
        return 'status-sold'
      default:
        return 'status-available'
    }
  }

  return (
    <motion.div
      className="modal-backdrop"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={handleBackdropClick}
    >
      <motion.div
        className="modal-content"
        variants={modalVariants}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <motion.button
          className="modal-close"
          onClick={onClose}
          whileHover={{ scale: 1.1, rotate: 90 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </motion.button>

        <div className="modal-body">
          {/* Image Section */}
          <div className="modal-image-section">
            <div className="modal-image-container">
              <img 
                src={puppy.image} 
                alt={`${puppy.name} - ${puppy.breed} puppy`}
                className="modal-image"
              />
              
              {/* Status Badge */}
              <div className={`modal-status-badge ${getStatusColor(puppy.status)}`}>
                <span className="status-text">{puppy.status}</span>
              </div>

              {/* Sold Overlay for sold puppies */}
              {puppy.status === 'Sold' && (
                <div className="modal-sold-overlay">
                  <div className="sold-content">
                    <span className="sold-text">Sold</span>
                    <span className="sold-date">
                      {puppy.soldDate && new Date(puppy.soldDate).toLocaleDateString()}
                    </span>
                    {puppy.newFamily && (
                      <span className="new-family">to {puppy.newFamily}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Info Section */}
          <div className="modal-info-section">
            <div className="modal-header">
              <div>
                <h2 className="modal-puppy-name">{puppy.name}</h2>
                <p className="modal-puppy-subtitle">{puppy.breed} {puppy.gender} • {puppy.age}</p>
              </div>
              <div className="modal-price">{puppy.price}</div>
            </div>

            <div className="modal-description">
              <p>{puppy.description}</p>
            </div>

            {/* Details Grid */}
            <div className="modal-details-grid">
              <div className="detail-card">
                <h4>Basic Information</h4>
                <div className="detail-row">
                  <span className="label">Gender:</span>
                  <span className="value">{puppy.gender}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Age:</span>
                  <span className="value">{puppy.age}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Registration:</span>
                  <span className="value">{puppy.registration}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Breed:</span>
                  <span className="value">{puppy.breed}</span>
                </div>
                {/* Weight removed as per request */}
              </div>

              <div className="detail-card">
                
                <div className="detail-row">
                  <span className="label">Sire:</span>
                  <span className="value">{puppy.sire}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Dam:</span>
                  <span className="value">{puppy.dam}</span>
                </div>
                <div className="detail-row">
              
                </div>
              </div>

              <div className="detail-card">
                <h4>Health & Care</h4>
                <div className="detail-row">
                  <span className="label">Vaccinations:</span>
                  <span className="value">{puppy.vaccinations}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Health:</span>
                  <span className="value">{puppy.health}</span>
                </div>
              </div>
            </div>

            {/* Temperament */}
            {puppy.temperament && (
              <div className="modal-temperament">
                <h4>Temperament</h4>
                <div className="temperament-tags">
                  {puppy.temperament.split(', ').map((trait, i) => (
                    <motion.span
                      key={i}
                      className="temperament-tag"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      {trait}
                    </motion.span>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="modal-actions">
              {puppy.status === 'Available' && (
                <motion.button
                  className="btn btn-primary btn-lg"
                  onClick={handleInquiry}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  Inquire About {puppy.name}
                </motion.button>
              )}
              
              {puppy.status === 'Reserved' && (
                <div className="reserved-info">
                  <p>This puppy is currently reserved but feel free to contact us about our upcoming litters!</p>
                  <motion.button
                    className="btn btn-secondary btn-lg"
                    onClick={handleInquiry}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Contact About Future Litters
                  </motion.button>
                </div>
              )}

              {puppy.status === 'Sold' && (
                <div className="sold-info">
                  <p>This beautiful puppy has found their forever home! Check out our available puppies or contact us about upcoming litters.</p>
                  <motion.button
                    className="btn btn-secondary btn-lg"
                    onClick={handleInquiry}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    View Available Puppies
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default PuppyModal